import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '2rem',
          fontFamily: 'monospace',
          maxWidth: '800px',
          margin: '2rem auto',
          background: '#fff',
          color: '#333',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}>
          <h1 style={{ color: '#e53e3e', fontSize: '1.5rem', marginBottom: '1rem' }}>
            Something went wrong
          </h1>
          <div style={{
            background: '#fef2f2',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.875rem',
          }}>
            <strong>Error:</strong> {this.state.error?.message}
          </div>
          {this.state.error?.stack && (
            <details open style={{ marginBottom: '1rem' }}>
              <summary style={{ cursor: 'pointer', color: '#666' }}>Stack trace</summary>
              <pre style={{
                background: '#f7f7f7',
                padding: '1rem',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '0.75rem',
                marginTop: '0.5rem',
              }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}
          {this.state.errorInfo?.componentStack && (
            <details open>
              <summary style={{ cursor: 'pointer', color: '#666' }}>Component stack</summary>
              <pre style={{
                background: '#f7f7f7',
                padding: '1rem',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '0.75rem',
                marginTop: '0.5rem',
              }}>
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#3182ce',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
